import { useState } from 'react';
import {
  IoInformationCircleOutline,
  IoWarningOutline,
  IoPricetagOutline,
  IoConstructOutline,
  IoChevronDown,
} from 'react-icons/io5';
import type { Announcement } from '@pelipost/consumer-api-client';
import { cn } from '@/components/ui/utils';

/**
 * One announcement row with expand-in-place body.
 *
 * Row shows icon + title + publish date; tapping expands the body inline.
 * Matches the pattern mobile uses for announcement cards — avoids adding
 * a whole announcement detail route for v1. When we build the consumer
 * inbox (PEL-836) we can replace this with a real detail route.
 *
 * Icon/tone is chosen from `type` on the announcement.
 */

const TYPE_META: Record<
  Announcement['type'],
  { icon: typeof IoInformationCircleOutline; tone: string }
> = {
  info: {
    icon: IoInformationCircleOutline,
    tone: 'bg-[var(--info-light)] text-[var(--info)]',
  },
  warning: {
    icon: IoWarningOutline,
    tone: 'bg-[var(--warning-light)] text-[var(--warning)]',
  },
  promotion: {
    icon: IoPricetagOutline,
    tone: 'bg-[var(--cta-light)] text-[var(--cta)]',
  },
  maintenance: {
    icon: IoConstructOutline,
    tone: 'bg-[var(--background)] text-[var(--text-light)]',
  },
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export interface AnnouncementRowProps {
  announcement: Announcement;
  className?: string;
}

export function AnnouncementRow({ announcement, className }: AnnouncementRowProps) {
  const [expanded, setExpanded] = useState(false);
  const { icon: Icon, tone } = TYPE_META[announcement.type];

  return (
    <div
      className={cn(
        'border-b border-[var(--border)] last:border-b-0',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-3 min-h-[64px] text-left',
          'hover:bg-[var(--background)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]',
        )}
      >
        <span
          className={cn('inline-flex items-center justify-center w-9 h-9 rounded-full shrink-0', tone)}
          aria-hidden
        >
          <Icon size={18} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[14px] text-[var(--text)] truncate">
            {announcement.title}
          </p>
          <p className="text-[12px] text-[var(--text-muted)]">
            {formatDate(announcement.publishAt)}
          </p>
        </div>
        <IoChevronDown
          size={18}
          className={cn(
            'text-[var(--text-muted)] shrink-0 transition-transform',
            expanded && 'rotate-180',
          )}
          aria-hidden
        />
      </button>
      {expanded ? (
        <div className="px-4 pb-4 pt-0">
          <p className="text-[13px] text-[var(--text-light)] whitespace-pre-wrap leading-relaxed">
            {announcement.body}
          </p>
        </div>
      ) : null}
    </div>
  );
}
